/**
 * Swiper carousel initialization (functional, simplified)
 * Goals:
 *  - Use Swiper's own active class (.swiper-slide-active) instead of managing custom .is-active in JS.
 *  - Provide a reusable init function that reads optional data-* attributes for configuration.
 *  - Resolve navigation / pagination elements via:
 *      1) data attributes (preferred if present)
 *      2) closest section fallback
 *  - Keep accessible messages (German) using Swiper A11y module.
 *  - Allow lazy (deferred) initialization through an exported function if needed elsewhere.
 */
import Swiper from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';

/**
 * Resolve a control element by priority:
 * 1) data attribute linking (e.g. data-swiper-prev="id")
 * 2) same section as container
 */
function resolveControl(type, container, id) {
    if (id) {
        const selector = `[data-swiper-${type}="${id}"]`;
        const byData = document.querySelector(selector);
        if (byData) return byData;
    }
    const section = container.closest('section');
    if (!section) return null;
    if (type === 'prev') return section.querySelector('.swiper_carousel__swiper-button-prev');
    if (type === 'next') return section.querySelector('.swiper_carousel__swiper-button-next');
    if (type === 'pagination') return section.querySelector('.swiper-pagination_image-slider');
    return null;
}

/**
 * Initialize a single carousel element.
 */
function initSwiperCarousel(container) {
    if (container.__swiperInitialized) return; // idempotent
    const id = container.dataset.swiperId || null;

    const prevEl = resolveControl('prev', container, id);
    const nextEl = resolveControl('next', container, id);
    const paginationEl = resolveControl('pagination', container, id);

    new Swiper(container, {
        modules: [Navigation, Pagination, A11y],
        // Classic static configuration (adjust here if needed)
        slidesPerView: 'auto',
        centeredSlides: true,
        loop: true,
        spaceBetween: 0,
        a11y: {
            enabled: true,
            prevSlideMessage: 'Vorheriger Slide',
            nextSlideMessage: 'Nächster Slide',
            firstSlideMessage: 'Das ist der erste Slide',
            lastSlideMessage: 'Das ist der letzte Slide'
        },
        navigation: (prevEl && nextEl) ? { prevEl, nextEl } : undefined,
        pagination: paginationEl ? { el: paginationEl, clickable: true } : undefined
    });

    container.__swiperInitialized = true;
}

/**
 * Initialize all carousels currently in DOM.
 */
function initSwiperCarousels() {
    document.querySelectorAll('.swiper_carousel').forEach(initSwiperCarousel);
}

// Auto-init on DOM ready
document.addEventListener('DOMContentLoaded', initSwiperCarousels);

// Expose helper for dynamic content (optional)
window.initSwiperCarousels = initSwiperCarousels;